import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Languages } from "lucide-react";

import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

// ─── Types ───────────────────────────────────────────────────────────────────

interface TranslationRow {
  key: string;
  translations: { en: string; fr: string; de: string };
  createdAt: string;
  updatedAt: string;
}

interface TranslationFormState {
  key: string;
  en: string;
  fr: string;
  de: string;
}

const EMPTY_FORM: TranslationFormState = { key: "", en: "", fr: "", de: "" };

interface TranslationsResponse {
  data: TranslationRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ─── API helpers ─────────────────────────────────────────────────────────────

async function fetchTranslations(page: number, search: string): Promise<TranslationsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (search) params.set("search", search);
  const res = await fetch(`/api/admin/translations?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error("adminTranslations.errors.fetchFailed");
  return res.json();
}

async function createTranslation(data: {
  key: string;
  translations: { en: string; fr: string; de: string };
}): Promise<TranslationRow> {
  const res = await fetch("/api/admin/translations", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "adminTranslations.errors.unknownError" }));
    throw new Error(body.error ?? "adminTranslations.errors.createFailed");
  }
  return res.json();
}

async function updateTranslation(
  key: string,
  translations: { en: string; fr: string; de: string }
): Promise<TranslationRow> {
  const res = await fetch(`/api/admin/translations/${encodeURIComponent(key)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ translations }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "adminTranslations.errors.unknownError" }));
    throw new Error(body.error ?? "adminTranslations.errors.updateFailed");
  }
  return res.json();
}

async function deleteTranslation(key: string): Promise<void> {
  const res = await fetch(`/api/admin/translations/${encodeURIComponent(key)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "adminTranslations.errors.unknownError" }));
    throw new Error(body.error ?? "adminTranslations.errors.deleteFailed");
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminTranslations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);

  // Pagination
  const [page, setPage] = useState(1);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<TranslationFormState>(EMPTY_FORM);

  // Delete confirmation
  const [deleteKey, setDeleteKey] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // ─── Query ─────────────────────────────────────────────────────────────────

  const { data, isLoading, isError } = useQuery({
    queryKey: ["/api/admin/translations", page, debouncedSearch],
    queryFn: () => fetchTranslations(page, debouncedSearch),
  });

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: createTranslation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/translations"] });
      toast({ title: "Translation created" });
      setDialogOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, translations }: { key: string; translations: { en: string; fr: string; de: string } }) =>
      updateTranslation(key, translations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/translations"] });
      toast({ title: "Translation updated" });
      setDialogOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTranslation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/translations"] });
      toast({ title: "Translation deleted" });
      setDeleteKey(null);
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  function openCreateDialog() {
    setEditingKey(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEditDialog(row: TranslationRow) {
    setEditingKey(row.key);
    setForm({
      key: row.key,
      en: row.translations.en ?? "",
      fr: row.translations.fr ?? "",
      de: row.translations.de ?? "",
    });
    setDialogOpen(true);
  }

  function handleFormSubmit() {
    if (!form.key.trim()) {
      toast({ title: "Key is required", variant: "destructive" });
      return;
    }
    const translations = { en: form.en, fr: form.fr, de: form.de };
    if (editingKey !== null) {
      updateMutation.mutate({ key: editingKey, translations });
    } else {
      createMutation.mutate({ key: form.key.trim(), translations });
    }
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  const translationColumns: ColumnDef<TranslationRow>[] = [
    {
      id: "key",
      header: "Key",
      headerClassName: "w-[30%]",
      cell: (row) => <span className="font-mono text-xs text-slate-900">{row.key}</span>,
    },
    {
      id: "en",
      header: "EN",
      cell: (row) => <span className="text-sm text-slate-700">{row.translations.en}</span>,
    },
    {
      id: "fr",
      header: "FR",
      cell: (row) => <span className="text-sm text-slate-700">{row.translations.fr}</span>,
    },
    {
      id: "de",
      header: "DE",
      cell: (row) => <span className="text-sm text-slate-700">{row.translations.de}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "w-[100px]",
      align: "right",
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-xl"
            onClick={() => openEditDialog(row)}
          >
            <Pencil className="h-3.5 w-3.5 text-primary" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => setDeleteKey(row.key)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{"Translations"}</h1>
          <p className="text-slate-500 mt-2">
            {"Manage application translation keys (EN / FR / DE)"}
          </p>
        </div>
        <Button onClick={openCreateDialog} className="flex items-center gap-2 rounded-2xl bg-primary hover:bg-primary/80 font-bold shadow-[0px_4px_6px_-4px_rgba(0,68,76,0.20),0px_10px_15px_-3px_rgba(0,68,76,0.20)]">
          <Plus className="h-4 w-4" />
          {"Add Translation"}
        </Button>
      </div>

      <div className="bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
        <div className="px-6 py-5 border-b border-border/30">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <Languages className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground">
              {"Translations"}
            </h3>
          </div>
        </div>
        {isError ? (
          <p className="text-sm text-red-500 p-6">{"Failed to load translations."}</p>
        ) : (
          <div className="p-6">
            <DataTable
              columns={translationColumns}
              data={data?.data ?? []}
              getRowId={(row) => row.key}
              isLoading={isLoading}
              emptyState={{
                title: search.trim() !== "" ? "No keys match your search." : "No translation keys yet.",
              }}
              search={{
                value: search,
                onChange: setSearch,
                placeholder: "Search by key…",
              }}
              pagination={
                data
                  ? {
                      page,
                      pageSize: data.pageSize,
                      total: data.total,
                      totalPages: data.totalPages,
                      onPageChange: setPage,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {editingKey !== null ? "Edit Translation" : "Add Translation"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="t-key" className="text-slate-700">{"Key"}</Label>
              <Input
                id="t-key"
                placeholder={"e.g. nav.dashboard"}
                value={form.key}
                onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                disabled={editingKey !== null}
                className="bg-slate-50 rounded-2xl"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-en" className="text-slate-700">{"English (EN)"}</Label>
              <Textarea
                id="t-en"
                placeholder={"English value"}
                value={form.en}
                onChange={(e) => setForm((f) => ({ ...f, en: e.target.value }))}
                className="bg-slate-50 rounded-2xl resize-none"
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-fr" className="text-slate-700">{"French (FR)"}</Label>
              <Textarea
                id="t-fr"
                placeholder={"French value"}
                value={form.fr}
                onChange={(e) => setForm((f) => ({ ...f, fr: e.target.value }))}
                className="bg-slate-50 rounded-2xl resize-none"
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-de" className="text-slate-700">{"German (DE)"}</Label>
              <Textarea
                id="t-de"
                placeholder={"German value"}
                value={form.de}
                onChange={(e) => setForm((f) => ({ ...f, de: e.target.value }))}
                className="bg-slate-50 rounded-2xl resize-none"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-2xl" onClick={() => setDialogOpen(false)}>
              {"Cancel"}
            </Button>
            <Button className="rounded-2xl" onClick={handleFormSubmit} disabled={isMutating}>
              {isMutating
                ? "Saving…"
                : editingKey !== null
                  ? "Save Changes"
                  : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteKey !== null}
        onOpenChange={(open) => { if (!open) setDeleteKey(null); }}
      >
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-slate-900">{"Delete translation key?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              {"This will permanently delete the key"}{" "}
              <span className="font-mono font-semibold text-slate-900">{deleteKey}</span>{" "}
              {"and all"}
              {" "}
              {"its translations. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">{"Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 rounded-2xl"
              onClick={() => deleteKey && deleteMutation.mutate(deleteKey)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
